import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl, uploadFile } from "@/utils";
import { Creator } from "@/entities/Creator";
import { User } from "@/entities/User";
import { PlusCircle, Pencil, Trash2, X, ArrowLeft, ImagePlus, Check, AlertCircle, Star, MapPin, Youtube } from "lucide-react";

const CREATOR_CATEGORIES = ["Artist (Entertainment)", "Content Creator", "Craftsman (Build To Spec)", "Healer (Remedies; Protocol)", "Manifestor", "Motivator", "Soul Worker (Passion Service)", "Problem Solver", "Visionary"];

const EMPTY_FORM = { name: "", bio: "", specialty: "", primary_category: "", location: "", avatar: "", banner_image: "", social_instagram: "", social_twitter: "", youtube_video_url: "", is_featured: false, rating: "", total_sales: 0, joined_date: "" };

function CreatorForm({ creator, onSave, onClose }) {
  const [form, setForm] = useState(creator || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleUpload = async (e, field, setUploading) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const { url } = await uploadFile(file, { isPublic: true });
    set(field, url);
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = { ...form, rating: form.rating ? parseFloat(form.rating) : null, total_sales: parseInt(form.total_sales) || 0 };
    if (creator?.id) await Creator.update(creator.id, data);
    else await Creator.create(data);
    setSaving(false);
    onSave();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#e8e4de] px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-display text-xl font-bold text-[#1a1a1a]">{creator ? "Edit Creator" : "Add Creator"}</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400 hover:text-[#1a1a1a]" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Avatar */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Avatar Photo</label>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-[#f5f3ef] border border-[#e8e4de] flex items-center justify-center">
                {form.avatar ? <img src={form.avatar} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-6 h-6 text-gray-300" />}
              </div>
              <label className="cursor-pointer text-sm text-[#c8a96e] font-medium hover:underline">
                {uploadingAvatar ? "Uploading..." : "Upload Avatar"}
                <input type="file" accept="image/*" className="hidden" onChange={e => handleUpload(e, "avatar", setUploadingAvatar)} disabled={uploadingAvatar} />
              </label>
            </div>
          </div>

          {/* Banner */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Banner Image</label>
            <div className="relative h-24 rounded-xl overflow-hidden bg-[#f5f3ef] border border-[#e8e4de] flex items-center justify-center">
              {form.banner_image ? <img src={form.banner_image} alt="" className="w-full h-full object-cover" /> : <ImagePlus className="w-6 h-6 text-gray-300" />}
              <label className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity cursor-pointer rounded-xl">
                <span className="text-white text-xs font-medium">{uploadingBanner ? "Uploading..." : "Change Banner"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={e => handleUpload(e, "banner_image", setUploadingBanner)} disabled={uploadingBanner} />
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Name *</label>
            <input required value={form.name} onChange={e => set("name", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="Creator or studio name" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Specialty *</label>
            <input required value={form.specialty} onChange={e => set("specialty", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="e.g. Watercolor Art, Jewelry" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Primary Category</label>
            <select value={form.primary_category} onChange={e => set("primary_category", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#c8a96e]">
              <option value="">Select a category...</option>
              {CREATOR_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <p className="text-xs text-gray-400 mt-1">The creator's primary type — shown on their profile and used for filtering.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Bio *</label>
            <textarea required value={form.bio} onChange={e => set("bio", e.target.value)} rows={3} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e] resize-none" placeholder="Short bio about this creator..." />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Location</label>
            <input value={form.location} onChange={e => set("location", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="City, State" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Instagram</label>
              <input value={form.social_instagram} onChange={e => set("social_instagram", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="@handle" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Twitter/X</label>
              <input value={form.social_twitter} onChange={e => set("social_twitter", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="@handle" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Rating (0-5)</label>
              <input type="number" min="0" max="5" step="0.1" value={form.rating} onChange={e => set("rating", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="4.8" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Total Sales</label>
              <input type="number" min="0" value={form.total_sales} onChange={e => set("total_sales", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Joined Date</label>
            <input type="date" value={form.joined_date} onChange={e => set("joined_date", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 flex items-center gap-1.5">
              <Youtube className="w-3.5 h-3.5 text-red-500" /> YouTube Intro Video URL
            </label>
            <input
              type="url"
              value={form.youtube_video_url}
              onChange={e => set("youtube_video_url", e.target.value)}
              className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]"
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <p className="text-xs text-gray-400 mt-1">Paste the full YouTube video URL for this creator's bio/intro video.</p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.is_featured} onChange={e => set("is_featured", e.target.checked)} className="w-4 h-4 accent-[#c8a96e]" />
            <span className="text-sm text-gray-700">Feature on Homepage</span>
          </label>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-[#e8e4de] text-[#1a1a1a] py-2.5 rounded-full text-sm font-medium hover:bg-[#f5f3ef] transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#1a1a1a] hover:bg-[#c8a96e] text-white py-2.5 rounded-full text-sm font-semibold transition-colors flex items-center justify-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
              {saving ? "Saving..." : creator ? "Save Changes" : "Add Creator"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CreatorManager() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editCreator, setEditCreator] = useState(null);
  const [unauthorized, setUnauthorized] = useState(false);

  useEffect(() => {
    async function load() {
      const me = await User.me();
      if (me?.role !== "admin") { setUnauthorized(true); setLoading(false); return; }
      const data = await Creator.list("-total_sales", 100);
      setCreators(data);
      setLoading(false);
    }
    load();
  }, []);

  const refresh = async () => {
    const data = await Creator.list("-total_sales", 100);
    setCreators(data);
    setFormOpen(false);
    setEditCreator(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this creator? Their products will remain but be unlinked.")) return;
    await Creator.delete(id);
    setCreators(c => c.filter(x => x.id !== id));
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#c8a96e] border-t-transparent rounded-full" /></div>;
  if (unauthorized) return <div className="flex flex-col items-center justify-center min-h-[60vh] text-center"><AlertCircle className="w-12 h-12 text-red-400 mb-4" /><h2 className="font-display text-2xl font-bold mb-2">Admin Access Only</h2></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center gap-3 mb-2">
        <Link to={createPageUrl("Admin")} className="text-gray-400 hover:text-[#c8a96e] transition-colors"><ArrowLeft className="w-4 h-4" /></Link>
        <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest">Admin · Creators</p>
      </div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-3xl font-bold text-[#1a1a1a]">Creator Manager</h1>
        <button onClick={() => { setEditCreator(null); setFormOpen(true); }} className="inline-flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#c8a96e] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors">
          <PlusCircle className="w-4 h-4" /> Add Creator
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {creators.map(c => (
          <div key={c.id} className="bg-white border border-[#e8e4de] rounded-xl overflow-hidden">
            <div className="relative h-28 overflow-hidden">
              <img src={c.banner_image || "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=300&fit=crop"} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              {c.is_featured && <span className="absolute top-2 right-2 bg-[#c8a96e] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Featured</span>}
              <div className="absolute bottom-2 left-3">
                <img src={c.avatar} alt={c.name} className="w-10 h-10 rounded-full border-2 border-white object-cover" />
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <h3 className="font-semibold text-[#1a1a1a] text-sm">{c.name}</h3>
                  <p className="text-xs text-[#c8a96e]">{c.specialty}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 fill-[#c8a96e] text-[#c8a96e]" />
                  <span className="text-xs font-semibold">{c.rating}</span>
                </div>
              </div>
              {c.location && <p className="text-xs text-gray-400 flex items-center gap-1 mb-2"><MapPin className="w-3 h-3" />{c.location}</p>}
              <p className="text-xs text-gray-500 line-clamp-2 mb-3">{c.bio}</p>
              <div className="flex items-center justify-between pt-3 border-t border-[#e8e4de]">
                <span className="text-xs text-gray-400">{c.total_sales} sales</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setEditCreator(c); setFormOpen(true); }} className="p-1.5 text-gray-400 hover:text-[#c8a96e] transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleDelete(c.id)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {creators.length === 0 && <div className="col-span-3 text-center py-16 text-gray-400">No creators yet. Add your first one!</div>}
      </div>

      {formOpen && <CreatorForm creator={editCreator} onSave={refresh} onClose={() => { setFormOpen(false); setEditCreator(null); }} />}
    </div>
  );
}